<template>
  <div class="panel panel-member-detail-map">
    <div class="panel-content">
      <map-control
        ref="map-control"
        class="map-control"
        v-bind="$store.getters.mapControlBinds"
        v-bind:id-prefix="mapIdPrefix"
        v-bind:center="userCenter"
        v-bind:zoom="18"
        v-bind:store-sync="['mapType']"
        v-bind:control="{ header: false, layerMenu: false }"
        v-bind:can-change-indoor-level="false"
        v-bind:layer-renderers="renderers"
        v-bind:reactive-render="reactiveRender"
        v-on:map-control-initend="$_onMapInitEnd"
      />
    </div>
  </div>
</template>

<script>
import DomainRenderer from '@/js/map-renderer/DomainRenderer.js'
import { mapState } from 'vuex'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

// レイヤーID
const LAYER_ID = 'member'

// レンダラーファクトリ
const rendererFactory = new DomainRenderer({ layerId: LAYER_ID })

export default {
  name: 'AppPanelMemberDetailMap',
  props: {
    /**
     * ユーザーデータ APIのレスポンス
     */
    data: {
      type: Object,
      default: null,
    },
    /**
     * リアクティブに描画する場合はtrue
     *
     * ダイアログが非表示の間は描画を止めるために使う
     */
    reactiveRender: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      /**
       * ユーザーデータ
       * @type {Object}
       */
      userStatus: null,
      /**
       * レイヤー描画パラメータ
       * @type {Array.Object}
       */
      layerRenderParameters: [],
      /**
       * レイヤーレンダラー
       * @type {Array.Object}
       */
      layerRenderers: [],
      /**
       * モーダルが重畳している場合はtrue
       * @type {Boolean}
       */
      isModalOverlaid: false,
      /**
       * 自レイヤーのレンダラー
       * @type {Object}
       */
      myRenderer: null,
    }
  },
  computed: {
    /**
     * 地図要素のIDプレフィックス
     *
     * 同じ画面に本コンポーネントが複数載るため、インスタンスごとに一意にする
     * @type {String}
     */
    mapIdPrefix: function () {
      return `member-profile-${this._uid}-`
    },
    ...mapState('userMarker', ['iconAtlas', 'iconMapping']),
    /**
     * 地図上のユーザー位置（データがなければstoreの値を参照）
     * @type {Object}
     */
    userCenter: function () {
      if (
        this.data &&
        this.data.geom &&
        this.data.geom.geometry &&
        this.data.geom.geometry.coordinates
      ) {
        const [lng, lat] = this.data.geom.geometry.coordinates
        return { lat, lng }
      }
      return this.$store.getters['map/center']
    },
    /**
     * 地図binding用render関数配列
     * @type {Array.<Function>}
     */
    renderers: function () {
      const renderers = [...this.$store.getters['map/layerRenderers']]
      if (this.myRenderer) {
        renderers.push(this.myRenderer.render)
      }
      return renderers
    },
  },
  watch: {
    data: function () {
      // ダイアログを閉じている間はデータが無い状態になるため更新しない
      if (!this.data) return
      this.$_updatePinImage()
        .then(() => this.$store.dispatch('userMarker/updateTexture'))
        .then(() => this.$_updateUserLayer())
    },
  },
  mounted: function () {
    this.$_updateMapLayers()
  },
  methods: {
    /**
     * 地図上に表示するピン画像データを更新
     */
    $_updatePinImage: async function () {
      if (!this.data.id) return

      try {
        await this.$store.dispatch('userMarker/getImageById', {
          id: this.data.id,
        })
      } catch (error) {
        this.handleApiError(error, () =>
          this.$log.warn(
            `failed to update pin image of user ID ${this.data.id}.`
          )
        )
      }
    },

    /**
     * ユーザーレイヤー更新
     */
    $_updateUserLayer: function () {
      const props = {
        iconAtlas: this.iconAtlas,
        iconMapping: this.iconMapping,
        getIcon: (d) => {
          if (this.iconMapping[`${d.properties.id}`]) {
            return `${d.properties.id}`
          }
          return 'default'
        },
        updateTriggers: {
          getIcon: [this.iconMapping],
        },
      }
      this.myRenderer = rendererFactory.createRenderer([this.data], props)
    },

    /**
     * 地図初期化完了時処理
     */
    $_onMapInitEnd: function () {
      this.map = this.$refs['map-control']
    },

    /**
     * マップレイヤー更新
     */
    $_updateMapLayers: async function () {
      try {
        await this.$store.dispatch('mapLayer/updateMapLayersWithThrottle')
      } catch (error) {
        this.handleApiError(error, () =>
          this.$log.warn('failed to fetch maplayers. use fetched before.')
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.panel {
  width: 100%;
  height: 100%;
  color: $text-color;
  background-color: $dialog-background-color;
}

.panel-member-detail-map {
  padding: 22px;
}

.panel-content {
  width: 100%;
  height: 100%;
  border: 1px solid #cccccc;
}
</style>
